import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { retailCategoryGroupsTooltip } from "../../../constants/TooltipInformation";
import { fieldValidationHelperText } from "../../../constants/validationHelperText";
import { RetailPropertyEnum } from "../../../constants/NavbarList";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const RetailCategoryGroups = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [categoryGroup, setCategoryGroup] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [nameError, setNameError] = useState<boolean>(false);
  const [retailCategoryGroups, setRetailCategoryGroups] = useState<any>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    "Category Group": categoryGroup,
    Active: active ? "Yes" : "No",
  };

  const validateInput = () => {
    setNameError(false);
    if (
      categoryGroup &&
      !validateString(
        categoryGroup,
        validationLength.categoryGroup,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      return false;
    }
    return true;
  };

  const editRetailCategoryGroups = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setCategoryGroup(null);
      setActive(true);
      setNameError(false);
      return;
    }
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setCategoryGroup(list?.["Category Group"] ?? null);
    setActive(list?.["Active"] === "Yes");
  };

  const updateRetailCategoryGroups = () => {
    if (!categoryGroup || String(categoryGroup).trim() === "") {
      setAlertMessage("Category group is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailCategoryGroups.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.CATEGORYGROUP,
        categoryGroup,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setRetailCategoryGroups((prev: any) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editRetailCategoryGroups(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${RetailPropertyEnum.categoryGroups} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRetailCategoryGroups = () => {
    if (!categoryGroup || String(categoryGroup).trim() === "") {
      setAlertMessage("Category group is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailCategoryGroups,
        DNDFieldsEnum.CATEGORYGROUP,
        categoryGroup,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailCategoryGroups((prev: any) =>
      updateData(
        prev !== null && prev?.length
          ? [
              ...prev,
              {
                id: retailCategoryGroups.length + 1,
                ...payload,
              },
            ]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRetailCategoryGroups(null);
    setEditClicked(false);
  };

  const deleteRetailCategoryGroups = (id: number) => {
    setRetailCategoryGroups(deleteItem(retailCategoryGroups, id));
  };

  useEffect(() => {
    data(retailCategoryGroups);
    editClicked &&
      setSelectedId(
        checkData(retailCategoryGroups, selectedId) ? selectedId : null,
      );
  }, [retailCategoryGroups]);

  useEffect(() => {
    if (fieldValues !== undefined && fieldValues !== null) {
      setRetailCategoryGroups(fieldValues);
    }
  }, []);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Category Groups
              <Tooltip
                title={tooltipComponent(retailCategoryGroupsTooltip.header)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton click={() => editRetailCategoryGroups(null)} />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRetailCategoryGroups(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Category Group
              <Tooltip
                title={tooltipComponent(
                  retailCategoryGroupsTooltip.categoryGroup,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailCategoryGroup"
              variant="outlined"
              placeholder="Category Group"
              value={categoryGroup ?? ""}
              helperText={
                nameError
                  ? validationMessages.categoryGroup
                  : fieldValidationHelperText.categoryGroup
              }
              error={nameError}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCategoryGroup(e.target.value);
                setNameError(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Active?
              <Tooltip
                title={tooltipComponent(retailCategoryGroupsTooltip.active)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label=""
            />
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={
                selectedId
                  ? updateRetailCategoryGroups
                  : addRetailCategoryGroups
              }
              id="addRetailCategoryGroup"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Category
              Group
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Category Groups Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {retailCategoryGroups?.length ? (
                <DraggableItemList
                  itemData={retailCategoryGroups}
                  onOrderUpdate={(order) => setRetailCategoryGroups(order)}
                  onEdit={(info) => {
                    editRetailCategoryGroups(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRetailCategoryGroups(id)}
                  code={DNDFieldsEnum.CATEGORYGROUP}
                  name={DNDFieldsEnum.ACTIVE}
                />
              ) : (
                <Typography variant="body2">
                  No Category Groups Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailCategoryGroups;
